import { Box, Button, Center, Grid, Select, Stack, Text } from '@mantine/core';
import { IconClockHour4, IconInbox, IconTrophy, IconUserQuestion } from '@tabler/icons-react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, type Page } from '../api/client';
import type { RequestRow, StatsOverview } from '../api/types';
import { BarsCard, dayRange, Kpi, REQUEST_BAR, statusBars, toneOfBar, TrendCard } from '../components/charts';
import { defaultRange, OverviewFilters, type RangeFilter } from '../components/OverviewFilters';
import { PageActions } from '../components/page-actions';
import { useDebounced } from '../components/useDebounced';
import { nameOf, tr } from '../lib/i18n';
import { fmtDate, fmtNum, REQUEST_STATUS, SOURCE } from '../lib/labels';
import { DataTable, tableDefaults } from '../ui/DataTable';
import { Code, ErrorState, Loading, Panel, SearchInput, StatusBadge, Toolbar } from '../ui/kit';

const ENTITY = 'REQUEST';

export function RequestsOverviewPage() {
  const [range, setRange] = useState(defaultRange());
  const stats = useQuery({
    queryKey: ['stats', range],
    queryFn: () => api<StatsOverview>('/stats/overview', { query: { from: range.from, to: range.to, organizationId: range.organizationId } }),
  });
  const s = stats.data;
  const mine = <T extends { entity: string }>(rows: T[] = []) => rows.filter((r) => r.entity === ENTITY);
  const days = s ? dayRange(s.range.from, s.range.to) : [];
  const byDay = new Map(mine(s?.series).map((r) => [r.day, r.total]));

  return (
    <Stack gap="lg">
      <PageActions><OverviewFilters value={range} onChange={setRange} /></PageActions>
      {stats.isLoading ? <Loading /> : stats.isError ? <ErrorState error={stats.error} onRetry={() => stats.refetch()} /> : s && (
        <>
          <Grid gap="lg">
            <Grid.Col span={{ base: 6, md: 3 }}><Kpi label={tr('طلبات جديدة', 'New requests')} hint={tr('في الفترة المختارة', 'In the selected period')} value={fmtNum(s.totals.requests)} icon={<IconInbox size={16} />} /></Grid.Col>
            <Grid.Col span={{ base: 6, md: 3 }}><Kpi label={tr('غير معيّنة الآن', 'Unassigned now')} value={fmtNum(s.live.unassignedRequests)} tone={s.live.unassignedRequests ? 'orange' : 'gray'} icon={<IconUserQuestion size={16} />} hint={tr('لحظياً', 'Live')} /></Grid.Col>
            <Grid.Col span={{ base: 6, md: 3 }}><Kpi label={tr('متوسط زمن التعيين', 'Avg. time to assign')} value={s.live.avgHoursToAssign === null ? '—' : tr(`${fmtNum(s.live.avgHoursToAssign)} ساعة`, `${fmtNum(s.live.avgHoursToAssign)} h`)} tone="blue" icon={<IconClockHour4 size={16} />} /></Grid.Col>
            <Grid.Col span={{ base: 6, md: 3 }}><Kpi label={tr('نسبة النجاح', 'Success rate')} value={`${fmtNum(s.conversionRate)}%`} tone="teal" icon={<IconTrophy size={16} />} /></Grid.Col>
            <Grid.Col span={12}>
              <TrendCard title={tr('الطلبات الجديدة يومياً', 'New requests per day')} days={days} series={[{ label: tr('الطلبات', 'Requests'), data: days.map((d) => byDay.get(d) ?? 0) }]} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}><BarsCard title={tr('حسب الحالة', 'By status')} rows={statusBars(mine(s.byStatus), REQUEST_STATUS, REQUEST_BAR)} /></Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}><BarsCard title={tr('حسب نوع الطلب', 'By request type')} rows={mine(s.byRequestType).map((r) => ({ label: nameOf(r), value: r.total }))} /></Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}><BarsCard title={tr('حسب المصدر', 'By source')} rows={mine(s.bySource).map((r) => ({ label: SOURCE[r.source] ?? r.source, value: r.total, color: r.source === 'PUBLIC_LINK' ? 'blue.5' : 'ink.9' }))} /></Grid.Col>
          </Grid>
          <Text size="xs" c="dimmed" mt={-8}>{tr('الأرقام من الإحصاءات اليومية وتُحدَّث كل 5 دقائق؛ «غير المعيّنة» و«زمن التعيين» محسوبة لحظياً.', 'Figures come from daily statistics and refresh every 5 minutes; “Unassigned” and “Time to assign” are calculated live.')}</Text>
        </>
      )}
      <RequestsTable range={range} />
    </Stack>
  );
}

function RequestsTable({ range }: { range: RangeFilter }) {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');
  const [assigned, setAssigned] = useState('');
  const dq = useDebounced(q);
  const list = useInfiniteQuery({
    queryKey: ['platform-requests', range, dq, status, source, assigned],
    queryFn: ({ pageParam }) =>
      api<Page<RequestRow>>('/requests', { query: { cursor: pageParam, limit: 25, q: dq, status, source, assignedTo: assigned, organizationId: range.organizationId, createdFrom: range.from, createdTo: range.to } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (p) => p.nextCursor ?? undefined,
  });
  const rows = list.data?.pages.flatMap((p) => p.items) ?? [];
  const filtered = !!(dq || status || source || assigned);
  return (
    <Panel flush title={tr('الطلبات', 'Requests')} description={tr('كل الطلبات الواردة في الفترة عبر كل الشركات', 'Every request received in the period, across companies')}>
      <Toolbar>
        <SearchInput value={q} onChange={(e) => setQ(e.currentTarget.value)} placeholder={tr('ابحث بالمرجع أو العميل أو الهاتف…', 'Search reference, client or phone…')} w={{ base: '100%', sm: 280 }} />
        <Select w={{ base: '100%', sm: 160 }} value={status} onChange={(v) => setStatus(v ?? '')}
          data={[{ value: '', label: tr('كل الحالات', 'All statuses') }, ...Object.entries(REQUEST_STATUS).map(([value, label]) => ({ value, label }))]} />
        <Select w={{ base: '48%', sm: 140 }} value={source} onChange={(v) => setSource(v ?? '')}
          data={[{ value: '', label: tr('كل المصادر', 'All sources') }, ...Object.entries(SOURCE).map(([value, label]) => ({ value, label }))]} />
        <Select w={{ base: '48%', sm: 150 }} value={assigned} onChange={(v) => setAssigned(v ?? '')}
          data={[{ value: '', label: tr('كل التعيينات', 'Any assignment') }, { value: 'unassigned', label: tr('غير معيّنة', 'Unassigned') }]} />
      </Toolbar>
      {list.isError ? <Box p="md"><ErrorState error={list.error} onRetry={() => list.refetch()} /></Box> : (
        <DataTable<RequestRow>
          {...(tableDefaults as object)}
          minHeight={rows.length ? undefined : 240}
          fetching={list.isLoading}
          records={rows}
          noRecordsText={filtered ? tr('لا توجد طلبات مطابقة', 'No matching requests') : tr('لا توجد طلبات في هذه الفترة', 'No requests in this period')}
          columns={[
            {
              accessor: 'ref_no',
              title: tr('المرجع', 'Reference'),
              noWrap: true,
              render: (r) => (
                <Box>
                  <Code>{r.ref_no}</Code>
                  {r.is_possible_duplicate && <StatusBadge tone="orange" size="xs" ms={6}>{tr('مكرر؟', 'Duplicate?')}</StatusBadge>}
                </Box>
              ),
            },
            { accessor: 'organization_name', title: tr('الشركة', 'Company'), render: (r) => <Text size="sm" fw={500}>{r.organization_name}</Text> },
            {
              accessor: 'request_type_name',
              title: tr('الطلب', 'Request'),
              render: (r) => (
                <Box>
                  <Text size="sm" lh={1.35}>{r.property_type_name ?? r.request_type_name}</Text>
                  {r.property_type_name && <Text size="xs" c="dimmed" lh={1.35}>{r.request_type_name}</Text>}
                </Box>
              ),
            },
            { accessor: 'locations', title: tr('الموقع', 'Location'), render: (r) => <Text size="sm" c={r.locations ? undefined : 'dimmed'} lineClamp={2}>{r.locations ?? '—'}</Text> },
            {
              accessor: 'budget',
              title: tr('الميزانية', 'Budget'),
              noWrap: true,
              render: (r) => r.budget_min || r.budget_max
                ? <Text size="sm" className="pf-num">{fmtNum(r.budget_min)} – {fmtNum(r.budget_max)} <Text span size="xs" c="dimmed">{r.currency}</Text></Text>
                : <Text c="dimmed">—</Text>,
            },
            {
              accessor: 'contact_name',
              title: tr('العميل', 'Client'),
              render: (r) => (
                <Box>
                  <Text size="sm" lh={1.35}>{r.contact_name}</Text>
                  <Text size="xs" c="dimmed" lh={1.35} dir="ltr" ta="start" className="pf-num">{r.contact_phone}</Text>
                </Box>
              ),
            },
            { accessor: 'status', title: tr('الحالة', 'Status'), noWrap: true, render: (r) => <StatusBadge tone={toneOfBar(REQUEST_BAR[r.status])}>{REQUEST_STATUS[r.status] ?? r.status}</StatusBadge> },
            {
              accessor: 'assigned_to_name',
              title: tr('المسؤول', 'Assignee'),
              render: (r) => r.assigned_to_name ? <Text size="sm">{r.assigned_to_name}</Text> : <Text size="sm" c="orange.7" fw={500}>{tr('غير معيّن', 'Unassigned')}</Text>,
            },
            {
              accessor: 'created_at',
              title: tr('الوصول', 'Received'),
              render: (r) => (
                <Box>
                  <Text size="sm" lh={1.35} style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.created_at)}</Text>
                  <Text size="xs" c="dimmed" lh={1.35}>{SOURCE[r.source]}</Text>
                </Box>
              ),
            },
          ]}
        />
      )}
      {list.hasNextPage && (
        <Center p="sm" style={{ borderTop: '1px solid var(--pf-line)' }}>
          <Button variant="default" onClick={() => list.fetchNextPage()} loading={list.isFetchingNextPage}>{tr('عرض المزيد', 'Show more')}</Button>
        </Center>
      )}
    </Panel>
  );
}
